// client/local-store — secret: IndexedDB schema, save/load, migration.
// Public: saveCharacter, loadCharacter, migrate.
//
// IDB is treated as best-effort: if open() hangs, errors, or is blocked, we
// continue without persistence rather than blocking boot. Save loop becomes a
// no-op until the next page load.

import { items } from '@tiermark/shared';

// NOTE: renamed from 'tiermark' to bypass a stuck DB at the previous v1 schema
// that caused indexedDB.open to hang without firing any callback. Old DB is
// best-effort deleted on boot.
const DB_NAME = 'tiermark-v2';
const DB_VERSION = 1;
const STORE_CHAR = 'characters';
const OPEN_TIMEOUT_MS = 8000;

export interface CharacterSave {
  id: string;
  name: string;
  position: { x: number; y: number; z: number };
  zoneId: string;
  createdAt: number;
  updatedAt: number;
  silver?: number;
  inventory?: items.Item[];
  equipment?: {
    mainHand?: items.Item | null;
    offHand?: items.Item | null;
    head?: items.Item | null;
    chest?: items.Item | null;
    shoes?: items.Item | null;
    cape?: items.Item | null;
  };
  fame?: { combat: number; gather: number; craft: number };
}

let dbPromise: Promise<IDBDatabase | null> | null = null;

function open(): Promise<IDBDatabase | null> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve) => {
    let settled = false;
    const finish = (db: IDBDatabase | null, reason?: string): void => {
      if (settled) return;
      settled = true;
      if (!db && reason) console.warn('[tiermark] local-store unavailable:', reason);
      resolve(db);
    };
    let req: IDBOpenDBRequest;
    try {
      req = indexedDB.open(DB_NAME, DB_VERSION);
    } catch (err) {
      finish(null, `indexedDB.open threw: ${String(err)}`);
      return;
    }
    req.onupgradeneeded = (): void => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_CHAR)) {
        db.createObjectStore(STORE_CHAR, { keyPath: 'id' });
      }
    };
    req.onsuccess = (): void => finish(req.result);
    req.onerror = (): void =>
      finish(null, `IDB open error: ${String(req.error)}`);
    req.onblocked = (): void =>
      finish(null, 'IDB upgrade blocked — close other Tiermark tabs and reload to enable saves.');
    setTimeout(() => {
      finish(null, `IDB open timed out after ${OPEN_TIMEOUT_MS}ms`);
    }, OPEN_TIMEOUT_MS);
  });
  return dbPromise;
}

// Best-effort cleanup of the pre-rename DB; fire-and-forget.
function deleteOldDb(): void {
  try {
    const req = indexedDB.deleteDatabase('tiermark');
    req.onsuccess = (): void => {};
    req.onerror = (): void => {};
    req.onblocked = (): void => {};
  } catch {
    // ignore
  }
}

export async function migrate(): Promise<void> {
  deleteOldDb();
  await open();
}

export async function saveCharacter(c: CharacterSave): Promise<void> {
  const db = await open();
  if (!db) return; // persistence unavailable; silently skip
  await new Promise<void>((resolve) => {
    try {
      const tx = db.transaction(STORE_CHAR, 'readwrite');
      tx.oncomplete = (): void => resolve();
      tx.onerror = (): void => resolve(); // log? we just skip this save
      tx.objectStore(STORE_CHAR).put({ ...c, updatedAt: Date.now() });
    } catch {
      resolve();
    }
  });
}

export async function loadCharacter(id: string): Promise<CharacterSave | null> {
  const db = await open();
  if (!db) return null;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE_CHAR, 'readonly');
      const req = tx.objectStore(STORE_CHAR).get(id);
      req.onsuccess = (): void => resolve((req.result as CharacterSave | undefined) ?? null);
      req.onerror = (): void => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

export async function loadAnyCharacter(): Promise<CharacterSave | null> {
  const db = await open();
  if (!db) return null;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE_CHAR, 'readonly');
      const req = tx.objectStore(STORE_CHAR).getAll();
      req.onsuccess = (): void => {
        const all = (req.result as CharacterSave[] | undefined) ?? [];
        if (all.length === 0) {
          resolve(null);
          return;
        }
        all.sort((a, b) => b.updatedAt - a.updatedAt);
        resolve(all[0] ?? null);
      };
      req.onerror = (): void => resolve(null);
    } catch {
      resolve(null);
    }
  });
}
